import {
  Box,
  Flex,
  Text,
  Modal,
  ModalContent,
  ModalOverlay,
  Image,
  Button,
  InputGroup,
  FormLabel,
  FormControl,
  InputRightElement,
} from "@chakra-ui/react";
import { memo, useEffect, useMemo, useState } from "react";
import IconX from "../../../assets/images/booking/x-circle.svg";
import {
  InputControl,
  SelectControl,
} from "../../../components/form/formControls";
import { useForm } from "react-hook-form";
import { useCategories } from "../../../categorys/registerCheckIn/detail";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { WorkingDayRates } from "../../../utils/contants";
import calendarIcon from "../../../assets/images/onleave/calendar.svg";
import DatePicker from "react-datepicker";

const schema = Yup.object().shape({
  employeeId: Yup.string().required("Vui lòng chọn nhân viên"),
  checkinPlace: Yup.string().required("Vui lòng nhập địa chỉ"),
  workingDayRate: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .typeError("Vui lòng nhập số công hợp lệ")
    .moreThan(0, "Số công phải lớn hơn 0")
    .required("Vui lòng nhập số công"),

  comment: Yup.string().required("Vui lòng nhập lý do"),
  checkinTime: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Ngày chấm công không hợp lệ.")
    .required("Vui lòng chọn ngày chấm công"),
});

const FormDetail = memo(
  ({ isOpen, onClose, onSubmit, isEdit, defaultValues }) => {
    const {
      reset,
      register,
      handleSubmit,
      watch,
      setValue,
      formState: { errors },
    } = useForm({
      resolver: yupResolver(schema),
      defaultValues: defaultValues || {},
    });

    const [shouldFetch, setShouldFetch] = useState(false);
    const [zIndex, setZIndex] = useState("");
    useEffect(() => {
      const timer = setTimeout(() => {
        setShouldFetch(true);
      }, 1200);

      return () => clearTimeout(timer);
    }, []);

    const { constantData } = useCategories(shouldFetch);

    const formatEntries = (entries) =>
      Object.entries(entries || {}).map(([key, value]) => ({
        value: key,
        label: value,
      }));

    const employees = useMemo(
      () => formatEntries(constantData?.employee),
      [constantData?.employee],
    );

    const [focusStates, setFocusStates] = useState({});

    useEffect(() => {
      reset(defaultValues);
    }, [isOpen]);

    const handleFocus = (field, data) => {
      if (data) {
        setZIndex(data);
      }
      setFocusStates((prev) => ({ ...prev, [field]: true }));
    };

    const handleBlur = (field, data) => {
      setZIndex("");
      setFocusStates((prev) => ({ ...prev, [field]: false }));
    };

    const onChangeEmployee = (option) => {
      setValue("employeeId", option);
    };

    const onchangeWorkingDayRate = (option) => {
      setValue("workingDayRate", option);
    };

    const handleDateChange = (selectedDate, inputName) => {
      setValue(inputName, selectedDate, { shouldValidate: true });
    };

    const submitHandler = (data) => {
      onSubmit(data);
    };

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent
          backgroundColor="var(--color-main)"
          borderRadius={"16px"}
          position={"relative"}
          w={"100%"}
          maxW={"625px"}
        >
          <form onSubmit={handleSubmit(submitHandler)}>
            <Box
              paddingBottom={"80px"}
              h={"86.5vh"}
              w={"100%"}
              overflowY={"scroll"}
              sx={{
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  width: "4px",
                  borderRadius: "50px",
                  background: "var(--color-background)",
                },
                "&::-moz-scrollbar": {
                  width: "4px",
                },
                "&::-moz-scrollbar-thumb": {
                  width: "4px",
                  borderRadius: "50px",
                  background: "var(--color-background)",
                },
              }}
            >
              <Flex
                alignItems={"center"}
                p={"25px 24px 24px 24px"}
                position={"relative"}
                borderBottom={"1px solid var(--border-input-employee)"}
              >
                <Text
                  color={"var(--color-header-employee)"}
                  fontSize={"16px"}
                  fontWeight={"600"}
                >
                  {isEdit
                    ? "Chỉnh sửa bản ghi chấm công"
                    : "Tạo bản ghi chấm công"}
                </Text>
                <Box
                  position={"absolute"}
                  top={"24px"}
                  right={"24px"}
                  cursor={"pointer"}
                  onClick={onClose}
                >
                  <Image src={IconX} />
                </Box>
              </Flex>
              <Box
                paddingTop={"24px"}
                paddingBottom={"41px"}
                maxW={"545px"}
                margin={"0 auto"}
              >
                <Box>
                  <Box mt={"16px"} borderRadius={"8px"}>
                    <Flex
                      justifyContent={"space-between"}
                      alignItems={"flex-start"}
                      gap={"27px"}
                    >
                      <SelectControl
                        label={"Nhân viên"}
                        dataOption={employees}
                        placeholder="Chọn nhân viên"
                        selectedMain={watch("employeeId")}
                        onChange={onChangeEmployee}
                        onFocus={() => handleFocus("employeeId")}
                        onBlur={() => handleBlur("employeeId")}
                        state={focusStates["employeeId"]}
                        registerProps={register("employeeId")}
                        errorMessage={errors.employeeId}
                        mwidth="100%"
                        isSearchable={true}
                        isRequired={true}
                      />
                    </Flex>
                    <Box mt={"16px"}>
                      <InputControl
                        label="Địa điểm"
                        placeholder="Nhập địa điểm"
                        errors={errors.checkinPlace}
                        register={register("checkinPlace")}
                        onFocus={() => handleFocus("checkinPlace")}
                        onBlur={() => handleBlur("checkinPlace")}
                        state={focusStates["checkinPlace"]}
                        mwidth="100%"
                        isRequired={true}
                      />
                    </Box>
                    <Box mt={"16px"}>
                      <InputControl
                        label="Lý do"
                        placeholder="Nhập lý do"
                        errors={errors.comment}
                        register={register("comment")}
                        onFocus={() => handleFocus("comment")}
                        onBlur={() => handleBlur("comment")}
                        state={focusStates["comment"]}
                        mwidth="100%"
                        isRequired={true}
                      />
                    </Box>
                    <Box
                      mt={"16px"}
                      zIndex={zIndex === "workingDayRate" ? 3 : 0}
                    >
                      <SelectControl
                        label="Số công"
                        dataOption={WorkingDayRates}
                        selectedMain={watch("workingDayRate")}
                        placeholder="Chọn số công"
                        onChange={onchangeWorkingDayRate}
                        onFocus={() => handleFocus("workingDayRate")}
                        onBlur={() => handleBlur("workingDayRate")}
                        state={focusStates["workingDayRate"]}
                        registerProps={register("workingDayRate")}
                        errorMessage={errors.workingDayRate}
                        mwidth={"100%"}
                        isRequired={true}
                      />
                    </Box>
                    <Flex
                      justifyContent={"space-between"}
                      alignItems={"flex-start"}
                      gap={"27px"}
                      mt={"16px"}
                    >
                      <FormControl zIndex={zIndex === "checkinTime" ? 4 : 0}>
                        <FormLabel
                          mb={"6px"}
                          fontSize={{ base: "12px" }}
                          fontWeight={400}
                          color={
                            focusStates["checkinTime"]
                              ? "var(--color-option-employee-hover)"
                              : "var(--color-label-employee)"
                          }
                        >
                          Thời gian chấm công{" "}
                          <span style={{ color: "red" }}>*</span>
                        </FormLabel>
                        <InputGroup>
                          <DatePicker
                            width="100%"
                            selected={watch("checkinTime")}
                            onChange={(date) =>
                              handleDateChange(date, "checkinTime")
                            }
                            dateFormat="dd/MM/yyyy"
                            locale="vi"
                            placeholderText="dd/mm/yy"
                            className="custom-datepicker datePickerStyle"
                            onFocus={() =>
                              handleFocus("checkinTime", "checkinTime")
                            }
                            onBlur={() => handleBlur("checkinTime")}
                          />
                          {/* <DatePicker
                            selected={
                              watch("checkinTime")
                                ? new Date(watch("checkinTime"))
                                : null
                            }
                            onChange={(date) =>
                              handleDateChange(date, "checkinTime")
                            }
                            showTimeSelect
                            dateFormat="dd/MM/yyyy HH:mm"
                            timeFormat="HH:mm"
                            locale="vi"
                            placeholderText="dd/mm/yyyy HH:mm"
                            className="custom-datepicker datePickerStyle"
                            onFocus={() =>
                              handleFocus("checkinTime", "checkinTime")
                            }
                            onBlur={() => handleBlur("checkinTime")}
                          /> */}

                          <InputRightElement pointerEvents="none" h={"44px"}>
                            <Image src={calendarIcon} />
                          </InputRightElement>
                        </InputGroup>
                        {errors.checkinTime && (
                          <Text fontSize={{ base: "12px" }} color={"red"}>
                            {errors.checkinTime.message}
                          </Text>
                        )}
                      </FormControl>
                    </Flex>
                  </Box>
                </Box>
              </Box>
              <Box
                position={"absolute"}
                w={"100%"}
                bottom={"0"}
                bg={"var(--color-main)"}
                borderTop={"0.5px solid var(--bg-line-employee)"}
                borderRadius={"0px 0px 16px 16px"}
                zIndex={3}
              >
                <Flex p={"24px"} gap={"16px"} justifyContent={"flex-end"}>
                  <Button
                    w={"112px"}
                    p={"13px 20px"}
                    fontSize={"15px"}
                    fontWeight={600}
                    color={"var(--color-option-employee-hover)"}
                    borderRadius={"8px"}
                    border={"1px solid var(--color-option-employee-hover)"}
                    bg={"var(--color-main)"}
                    _hover={{
                      filter: "brightness(90%)",
                    }}
                    onClick={onClose}
                  >
                    Hủy
                  </Button>
                  <Button
                    type="submit"
                    w={"112px"}
                    p={"13px 20px"}
                    fontSize={"15px"}
                    fontWeight={600}
                    color={"var(--color-main)"}
                    borderRadius={"8px"}
                    bg={"var(--color-option-employee-hover)"}
                    _hover={{
                      filter: "brightness(90%)",
                    }}
                  >
                    Lưu
                  </Button>
                </Flex>
              </Box>
            </Box>
          </form>
        </ModalContent>
      </Modal>
    );
  },
);

export default FormDetail;
